import type { BaseMetadata, ThreadData } from "@liveblocks/client";
import { useThreads } from "@liveblocks/react/suspense";
import { useAppSelector } from "@store";
import { getCurrentPayloadId } from "@store/story/selectors";
import { memo, useMemo } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { cn } from "@/lib/cn";
import { useGetUsersQuery } from "@/shared/services";
import type { IUser } from "@/types";
import { STORYDOC_AVATAR, STORYDOC_PREFIX, UNKNOWN_AVATAR } from "../constants";
import { CollaborationEnabledProvider } from "../providers";
import { Suspense } from "./Suspense";

export const EntityThreadLabel = memo(
  ({
    id,
    isActive,
    layer,
  }: {
    id: string;
    isActive: boolean;
    layer: string;
  }) => {
    return (
      <CollaborationEnabledProvider>
        <Suspense feature="Thread label">
          <Content id={id} isActive={isActive} layer={layer} />
        </Suspense>
      </CollaborationEnabledProvider>
    );
  },
);

const Content = ({
  id,
  isActive,
  layer,
}: {
  id: string;
  isActive: boolean;
  layer: string;
}) => {
  const storyId = useAppSelector(getCurrentPayloadId);
  const { data: users } = useGetUsersQuery();
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    freezeOnceVisible: true,
  });
  const { threads } = useThreads({
    query: {
      resolved: false,
      metadata: {
        storyId,
      },
    },
  });

  return (
    <div
      data-thread-label={layer}
      ref={ref}
      className="tw-absolute tw-left-0 tw-top-0 tw-z-[12] -tw-translate-x-1/2 -tw-translate-y-1/2 tw-transition-transform"
    >
      {isIntersecting && users && (
        <Label id={id} threads={threads} users={users as IUser[]} />
      )}
    </div>
  );
};

const Label = ({
  id,
  threads,
  users,
}: {
  id: string;
  threads: ThreadData<BaseMetadata>[] | undefined;
  users: IUser[];
}) => {
  const filtered = useMemo(() => {
    return threads?.filter((thread) => thread.metadata.entityId === id) || [];
  }, [threads, id]);
  const user = useMemo(() => {
    const userId = filtered?.[0]?.comments[0]?.userId;
    if (!userId) return null;

    if (userId.startsWith(STORYDOC_PREFIX)) {
      return {
        firstName: "Storydoc",
        profile: { picture: STORYDOC_AVATAR },
      };
    }

    return (
      users.find((user) => user._id === userId) || {
        firstName: "Anonymous",
        profile: { picture: UNKNOWN_AVATAR },
      }
    );
  }, [users, filtered]);

  const commentsCount = useMemo(() => {
    return filtered.reduce((acc, thread) => {
      const commentsWithBody =
        thread?.comments?.filter((comment) => comment.body) || [];
      return acc + commentsWithBody.length;
    }, 0);
  }, [filtered]);

  if (!user) return null;

  const initials = user?.firstName
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("");

  return (
    <div
      data-thread-label-id={id}
      className="tw-relative tw-flex tw-size-8 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-border tw-border-blue-base/10 tw-bg-white tw-shadow-sm"
      onClick={() => {
        setTimeout(() => {
          const el = document.querySelector(
            `[data-layer-id="${id}"] [data-comment-action-trigger] .WsA9Pt7X1Xr2kTA-trigger`,
          ) as HTMLElement;
          if (el) el.click();
        }, 0);
      }}
    >
      <div
        className={cn(
          "rw- tw-flex tw-size-[26px] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-blue-base/10 tw-bg-blue-200 tw-font-sans tw-text-[10px]/none tw-font-medium tw-text-blue-base",
          {
            "tw-bg-primary-base tw-text-white": commentsCount > 1,
          },
        )}
      >
        {commentsCount === 1 ? initials : `+${commentsCount}`}
        {user?.profile?.picture && commentsCount === 1 && (
          <img
            src={user.profile.picture}
            alt=""
            className="tw-absolute tw-left-0 tw-top-0 tw-size-full tw-object-cover"
          />
        )}
      </div>
    </div>
  );
};
